import SyosEvents from './utils/SyosEvents';
import SyosUiContainer from './SyosUiContainer';
import SyosSeat from './SyosSeat';
import Viewport from './utils/Viewport';
import SyosSeatMap from './SyosSeatMap';
import SyosGrid from './grid/SyosGrid';
import SeatClientRectProvider, { SeatClientRectContainerData } from './utils/SeatClientRectProvider';
import SyosSeatFocusIndicator from './SyosSeatFocusIndicator';
import { Screen, SeatListResult } from './api/types';
import type SyosController from './SyosController';
import type ViewportZoomControl from './utils/ViewportZoomControl';
import type IconRenderer from './seatIcons/IconRenderer';
import type { IPriceTypeSelectionHelper } from './priceTypeSelectionHelpers/types';

class SyosSeatContainer extends SyosUiContainer {
  private viewport?: Viewport;

  private focusIndicator?: SyosSeatFocusIndicator;

  private grid?: SyosGrid;

  private seats?: Map<number, SyosSeat>;

  seatRectProvider?: SeatClientRectProvider;

  constructor(syosController: SyosController, private zoomControl: ViewportZoomControl, private iconRenderer: IconRenderer, private priceTypeSelectionHelper: IPriceTypeSelectionHelper, private rootHeadingLevel: number) {
    super(syosController, '.tn-syos-seat-container');

    this.$el?.hide();

    this.$el![0].addEventListener('transitionend', this.onTransitionEnd);

    syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
    syosController.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatListDataLoaded);
    syosController.on(SyosEvents.SEAT_FOCUS, this.onSeatFocus);
    syosController.on(SyosEvents.SEAT_BLUR, this.onSeatBlur);
    syosController.on(SyosEvents.CART_UPDATE, this.onCartUpdate);
  }

  onTransitionEnd = (event: TransitionEvent) => {
    if (event.propertyName === 'width') {
      this.viewport?.updateContainerRect();
    }
  };

  onSeatFocus = ({ seat }: { seat: SyosSeat }) => {
    const clientRect = seat.getClientRect();

    if (!clientRect) {
      return;
    }

    this.viewport?.bringRectIntoView(clientRect, 0);

    this.focusIndicator?.show(seat);
  };

  onCartUpdate = () => {
    const { canMakeAnotherSelection } = this.priceTypeSelectionHelper;

    this.$el?.toggleClass('tn-syos-seat-container--selections-complete', !canMakeAnotherSelection);
  };

  onSeatBlur = () => {
    this.focusIndicator?.hide();
  };

  onScreenUpdated = ({ screen }: { screen: Screen }) => {
    if (screen) {
      this.load(screen.id);
    } else {
      this.hide();
    }
  };

  onSeatListDataLoaded = () => {
    this.show();
  };

  load = async (screenId: number) => {
    const dataService = this.syosController?.getDataService();

    if (!dataService) {
      return;
    }

    this.reset();

    const seatList = await dataService.getSeatList(screenId);

    this.render(seatList);
  };

  render({
    performanceId, facilityId, screenId, seats: seatData, nonSeats, rowCount, columnCount, stageOrientation,
  }: SeatListResult) {
    if (!this.syosController) {
      return;
    }

    const cart = this.syosController.getCartContainer()!;
    const dataService = this.syosController.getDataService()!;
    const screen = dataService.getScreen(screenId)!;
    const seats = new Map<number, SyosSeat>();

    const seatRectProvider = new SeatClientRectProvider(this);

    seatData.forEach((data) => {
      // Grab existing SyosSeat instance from cart if present
      let seat = cart.getSeatForId(data.id);
      if (!seat) {
        seat = new SyosSeat(this.syosController!, data, seatRectProvider);
      }
      seats.set(seat.id, seat);
    });

    this.seatRectProvider = seatRectProvider;

    nonSeats.forEach((data) => {
      data.x = SyosSeat.adjustValueForGrid(data.x);
      data.y = SyosSeat.adjustValueForGrid(data.y);
    });

    this.viewport = new Viewport(
      this.syosController.$el![0] as HTMLElement,
      this.onViewportUpdate,
      this.zoomControl,
    );

    const seatMap = new SyosSeatMap({
      rowCount,
      columnCount,
      unitSize: SyosSeat.GRID_UNIT_SIZE,
      marginSize: SyosSeat.GRID_MARGIN,
      seats: Array.from(seats.values()),
      nonSeats,
      stageOrientation,
      performanceId,
      facilityId,
      screenId,
      screenDescription: screen.description,
      iconRenderer: this.iconRenderer,
      rootHeadingLevel: this.rootHeadingLevel,
    });

    const { el: seatMapEl, width: seatMapWidth, height: seatMapHeight } = seatMap.render();

    Array.from(seatMapEl.querySelectorAll<SVGGElement>(`.${SyosSeat.CSS_CLASS}`)).forEach((seatEl) => {
      const seatId = parseInt(seatEl.getAttribute('data-tn-seat-id')!, 10);
      const seat = seats.get(seatId)!;
      seat.setEl(seatEl);
    });

    this.seats = seats;

    this.$el?.append(seatMapEl);

    this.$el?.append(this.viewport.el);
    this.viewport.setContentEl(seatMapEl, seatMapWidth, seatMapHeight);
    this.viewport.startListening();

    const $focusIndicatorEl = seatMapEl.querySelector<SVGGElement>('#tn-syos-seat-focus-outline')!;
    this.focusIndicator = new SyosSeatFocusIndicator($focusIndicatorEl);

    this.grid = new SyosGrid(this.viewport.el, [...this.seats.values()]);
  }

  onViewportUpdate = () => {
    this.syosController?.emit(SyosEvents.SEAT_MAP_ADJUST);
  };

  getSeatClientRectContainerData(): SeatClientRectContainerData {
    const { viewport } = this;

    if (!viewport) {
      return {
        scale: 1,
        clientRect: {
          top: 0, bottom: 0, left: 0, right: 0, width: 0, height: 0,
        },
      };
    }

    return {
      scale: viewport.scale,
      clientRect: viewport.getContentClientRect()!,
    };
  }

  reset() {
    this.seats?.forEach((seat) => seat.detach());

    this.viewport?.destroy();
    this.viewport = undefined;

    this.grid?.destroy();
    this.grid = undefined;

    this.seatRectProvider?.destroy();
    this.seatRectProvider = undefined;
  }

  hide() {
    this.$el?.hide();
    this.reset();
  }

  show() {
    this.$el?.show();
  }

  destroy() {
    this.reset();

    this.$el![0].removeEventListener('transitionend', this.onTransitionEnd);

    this.$el?.off();

    this.focusIndicator = undefined;

    super.destroy();
  }
}

export default SyosSeatContainer;
